import React, { useState, useEffect } from 'react';
import { GridProvider } from './contexts/GridContext';
import { PlantProvider } from './contexts/PlantContext';
import { SyncProvider } from '@monorepo/shared';
import { migrateNurserySpeciesToSharedDb } from './services/migration';
import GridView from './components/GridView';
import CellDetailPanel from './components/CellDetailPanel';
import PlantForm from './components/PlantForm';
import AddPlantForm from './components/AddPlantForm';
import PlantDetailView from './components/PlantDetailView';
import LogbookView from './components/LogbookView';
import LogEntryForm from './components/LogEntryForm';
import SearchView from './components/SearchView';
import GridConfigView from './components/GridConfigView';
import NFCWriteView from './components/NFCWriteView';
import NFCScanHandler from './components/NFCScanHandler';
import SyncIndicator from './components/SyncIndicator';
import PlantIDView from './components/PlantIDView';
import usePlants from './hooks/usePlants';
import useGrid from './hooks/useGrid';
import './App.css';

const VIEWS = {
  GRID: 'grid',
  SEARCH: 'search',
  CONFIG: 'config',
  NFC_WRITE: 'nfc-write',
  PLANT_ID: 'plant-id',
};

function GridViewWithDetail() {
  const { getPlantsByCell, addPlant, updatePlant, addLogEntry, getLogEntries, getPhotos, plants: allPlants, species: gridSpecies } = usePlants();
  const { gridConfig } = useGrid();
  const [selectedCell, setSelectedCell] = useState(null);
  const [panel, setPanel] = useState('list'); // 'list' | 'addPlant' | 'editPlant' | 'logbook' | 'addLogEntry' | 'viewPlant'
  const [editingPlantId, setEditingPlantId] = useState(null);
  const [logbookPlantId, setLogbookPlantId] = useState(null);
  const [logEntries, setLogEntries] = useState([]);
  const [viewingPlantId, setViewingPlantId] = useState(null);

  const handleCellSelect = (row, col) => {
    setSelectedCell({ row, col });
    setPanel('list');
  };

  const handleNFCPosition = (row, col) => {
    setSelectedCell({ row, col });
    setPanel('list');
  };

  const handleAddPlant = () => {
    setPanel('addPlant');
  };

  const handlePlantFormSubmit = async (plantData) => {
    if (editingPlantId) {
      await updatePlant(editingPlantId, plantData);
    } else {
      await addPlant(plantData);
    }
    setPanel('list');
    setEditingPlantId(null);
  };

  const handlePlantFormCancel = () => {
    setPanel('list');
    setEditingPlantId(null);
  };

  const handleEditPlant = (plantId) => {
    setEditingPlantId(plantId);
    setPanel('editPlant');
  };

  const handleViewLogbook = async (plantId) => {
    setLogbookPlantId(plantId);
    const entries = await getLogEntries(plantId);
    setLogEntries(entries);
    setPanel('logbook');
  };

  const handleAddLogEntry = () => {
    setPanel('addLogEntry');
  };

  const handlePlantDrop = async (plantId, newRow, newCol) => {
    await updatePlant(plantId, { gridRow: newRow, gridCol: newCol });
  };

  const handleViewPlant = (plantId) => {
    setViewingPlantId(plantId);
    setPanel('viewPlant');
  };

  const handleLogEntrySubmit = async ({ notes, photos, quantities }) => {
    if (quantities && logbookPlantId) {
      await updatePlant(logbookPlantId, quantities);
    }
    await addLogEntry(logbookPlantId, notes, photos);
    const entries = await getLogEntries(logbookPlantId);
    setLogEntries(entries);
    setPanel('logbook');
  };

  const cellPlants = selectedCell
    ? getPlantsByCell(selectedCell.row, selectedCell.col)
    : [];

  const editingPlant = editingPlantId
    ? allPlants.find(p => p.id === editingPlantId) || null
    : null;

  const logbookPlant = logbookPlantId
    ? allPlants.find(p => p.id === logbookPlantId) || null
    : null;

  const viewingPlant = viewingPlantId
    ? allPlants.find(p => p.id === viewingPlantId) || null
    : null;

  const renderDetailPanel = () => {
    if (!selectedCell) {
      return <p className="app__select-prompt">Select a cell to view details</p>;
    }

    switch (panel) {
      case 'addPlant':
        return (
          <AddPlantForm
            gridRow={selectedCell.row}
            gridCol={selectedCell.col}
            onSubmit={handlePlantFormSubmit}
            onCancel={handlePlantFormCancel}
          />
        );
      case 'editPlant':
        const editSpecies = editingPlant ? gridSpecies.find(s =>
          s.scientificName?.toLowerCase() === editingPlant.scientificName?.toLowerCase() || s.commonName?.toLowerCase() === editingPlant.commonName?.toLowerCase()
        ) : null;
        return (
          <PlantForm
            plant={editingPlant}
            gridRow={selectedCell.row}
            gridCol={selectedCell.col}
            onSubmit={handlePlantFormSubmit}
            onCancel={handlePlantFormCancel}
            speciesData={editSpecies}
          />
        );
      case 'logbook':
        return (
          <LogbookView
            entries={logEntries}
            onAddEntry={handleAddLogEntry}
            plantName={logbookPlant?.commonName || 'Plant'}
            getPhotos={getPhotos}
          />
        );
      case 'addLogEntry':
        return (
          <LogEntryForm
            onSubmit={handleLogEntrySubmit}
            onCancel={() => setPanel('logbook')}
            currentQuantities={logbookPlant ? {
              qtyPlugs: logbookPlant.qtyPlugs ?? 0,
              qtySoloCups: logbookPlant.qtySoloCups ?? 0,
              qtyQuarts: logbookPlant.qtyQuarts ?? 0,
              qtyGallons: logbookPlant.qtyGallons ?? 0,
            } : undefined}
          />
        );
      case 'viewPlant':
        const viewSpecies = viewingPlant ? gridSpecies.find(s =>
          s.scientificName?.toLowerCase() === viewingPlant.scientificName?.toLowerCase() || s.commonName?.toLowerCase() === viewingPlant.commonName?.toLowerCase()
        ) : null;
        return (
          <PlantDetailView
            plant={viewingPlant}
            onBack={() => setPanel('list')}
            onEdit={handleEditPlant}
            getLogEntries={getLogEntries}
            addLogEntry={addLogEntry}
            getPhotos={getPhotos}
            updatePlant={updatePlant}
            speciesData={viewSpecies}
          />
        );
      default:
        return (
          <CellDetailPanel
            row={selectedCell.row}
            col={selectedCell.col}
            plants={cellPlants}
            onAddPlant={handleAddPlant}
            onEditPlant={handleEditPlant}
            onViewLogbook={handleViewLogbook}
            onViewPlant={handleViewPlant}
          />
        );
    }
  };

  return (
    <div className="app__grid-layout">
      <div className="app__grid-main">
        <GridView onCellSelect={handleCellSelect} onPlantDrop={handlePlantDrop} />
        {gridConfig && (
          <NFCScanHandler
            onPositionRead={handleNFCPosition}
            gridConfig={gridConfig}
          />
        )}
      </div>
      <div className="app__grid-detail">
        {renderDetailPanel()}
      </div>
    </div>
  );
}

function AppContent() {
  const [currentView, setCurrentView] = useState(VIEWS.GRID);
  const { gridConfig } = useGrid();
  const { plants: allPlants, species: allSpecies, getLogEntries, addLogEntry, getPhotos, updatePlant, updateSpecies } = usePlants();
  const [detailPlantId, setDetailPlantId] = useState(null);
  const [editingPlantId, setEditingPlantId] = useState(null);

  const handleViewPlantFromSearch = (plantOrSpecies) => {
    // Can receive either a plant ID (string) or a species object
    if (typeof plantOrSpecies === 'string') {
      setDetailPlantId(plantOrSpecies);
    } else if (plantOrSpecies?.id) {
      // It's a species object — find a plant instance or use the species directly
      const matchingPlant = allPlants.find(p =>
        p.commonName?.toLowerCase() === plantOrSpecies.commonName?.toLowerCase() &&
        p.scientificName?.toLowerCase() === plantOrSpecies.scientificName?.toLowerCase()
      );
      if (matchingPlant) {
        setDetailPlantId(matchingPlant.id);
      } else {
        // No plant instance — show species detail directly
        setDetailPlantId('species:' + plantOrSpecies.id);
      }
    }
    setEditingPlantId(null);
  };

  const handleBackFromDetail = () => {
    setDetailPlantId(null);
    setEditingPlantId(null);
  };

  const handleEditFromDetail = (plantId) => {
    setEditingPlantId(plantId);
  };

  const handleEditSubmit = async (plantData) => {
    await updatePlant(editingPlantId, plantData);
    setEditingPlantId(null);
  };

  const handleEditCancel = () => {
    setEditingPlantId(null);
  };

  const detailPlant = detailPlantId
    ? detailPlantId.startsWith('species:')
      ? (() => {
          const speciesId = detailPlantId.slice(8);
          const sp = allSpecies.find(s => s.id === speciesId);
          return sp ? { ...sp, id: sp.id, speciesId: sp.id } : null;
        })()
      : allPlants.find(p => p.id === detailPlantId) || null
    : null;

  // Find all instances of the same species for the detail view
  const detailPlantInstances = detailPlant
    ? allPlants.filter(p =>
        p.commonName === detailPlant.commonName &&
        p.scientificName === detailPlant.scientificName
      )
    : [];

  // Look up species catalog data for the detail plant (case-insensitive)
  const detailSpeciesData = detailPlant
    ? allSpecies.find(s =>
        s.scientificName?.toLowerCase() === detailPlant.scientificName?.toLowerCase() ||
        s.commonName?.toLowerCase() === detailPlant.commonName?.toLowerCase()
      ) || null
    : null;

  const renderView = () => {
    // If editing a plant from search detail
    if (editingPlantId && currentView === VIEWS.SEARCH) {
      const editingPlant = allPlants.find(p => p.id === editingPlantId) || null;
      if (editingPlant) {
        const editSearchSpecies = allSpecies.find(s =>
          s.scientificName?.toLowerCase() === editingPlant.scientificName?.toLowerCase() || s.commonName?.toLowerCase() === editingPlant.commonName?.toLowerCase()
        ) || null;
        return (
          <PlantForm
            plant={editingPlant}
            gridRow={editingPlant.gridRow}
            gridCol={editingPlant.gridCol}
            onSubmit={handleEditSubmit}
            onCancel={handleEditCancel}
            speciesData={editSearchSpecies}
          />
        );
      }
    }

    // If viewing a plant detail from search, show it
    if (detailPlant && currentView === VIEWS.SEARCH) {
      return (
        <PlantDetailView
          plant={detailPlant}
          onBack={handleBackFromDetail}
          onEdit={handleEditFromDetail}
          getLogEntries={getLogEntries}
          addLogEntry={addLogEntry}
          getPhotos={getPhotos}
          updatePlant={updatePlant}
          allInstances={detailPlantInstances}
          updateSpecies={updateSpecies}
          speciesData={detailSpeciesData}
        />
      );
    }

    switch (currentView) {
      case VIEWS.GRID:
        return <GridViewWithDetail />;
      case VIEWS.SEARCH:
        return <SearchView onViewPlant={handleViewPlantFromSearch} />;
      case VIEWS.CONFIG:
        return <GridConfigView />;
      case VIEWS.NFC_WRITE:
        return <NFCWriteView gridConfig={gridConfig} />;
      case VIEWS.PLANT_ID:
        return <PlantIDView />;
      default:
        return <GridViewWithDetail />;
    }
  };

  return (
    <div className="app">
      <nav className="app__nav" aria-label="Main navigation">
        <button
          className={`app__nav-btn ${currentView === VIEWS.GRID ? 'app__nav-btn--active' : ''}`}
          onClick={() => { setCurrentView(VIEWS.GRID); setDetailPlantId(null); }}
          aria-current={currentView === VIEWS.GRID ? 'page' : undefined}
        >
          Grid
        </button>
        <button
          className={`app__nav-btn ${currentView === VIEWS.SEARCH ? 'app__nav-btn--active' : ''}`}
          onClick={() => { setCurrentView(VIEWS.SEARCH); setDetailPlantId(null); }}
          aria-current={currentView === VIEWS.SEARCH ? 'page' : undefined}
        >
          Search
        </button>
        <button
          className={`app__nav-btn ${currentView === VIEWS.CONFIG ? 'app__nav-btn--active' : ''}`}
          onClick={() => { setCurrentView(VIEWS.CONFIG); setDetailPlantId(null); }}
          aria-current={currentView === VIEWS.CONFIG ? 'page' : undefined}
        >
          Config
        </button>
        <button
          className={`app__nav-btn ${currentView === VIEWS.NFC_WRITE ? 'app__nav-btn--active' : ''}`}
          onClick={() => { setCurrentView(VIEWS.NFC_WRITE); setDetailPlantId(null); }}
          aria-current={currentView === VIEWS.NFC_WRITE ? 'page' : undefined}
        >
          NFC Write
        </button>
        <button
          className={`app__nav-btn ${currentView === VIEWS.PLANT_ID ? 'app__nav-btn--active' : ''}`}
          onClick={() => { setCurrentView(VIEWS.PLANT_ID); setDetailPlantId(null); }}
          aria-current={currentView === VIEWS.PLANT_ID ? 'page' : undefined}
        >
          ID
        </button>
      </nav>

      <main className="app__content">
        {renderView()}
      </main>
    </div>
  );
}

function App() {
  const [migrationDone, setMigrationDone] = useState(false);

  useEffect(() => {
    migrateNurserySpeciesToSharedDb().then(() => {
      setMigrationDone(true);
    });
  }, []);

  if (!migrationDone) {
    return null;
  }

  return (
    <GridProvider>
      <PlantProvider>
        <SyncProvider>
          <AppContent />
          <SyncIndicator />
        </SyncProvider>
      </PlantProvider>
    </GridProvider>
  );
}

export default App;
